import React, { useEffect, useState } from "react";
import { ShieldCheck, Instagram } from "lucide-react";
import { crmApi } from "../../lib/crmApi";
import { Field, inputCls } from "./crmUi";

const FIELDS = [
  { key: "instagram_access_token", label: "Instagram Access Token", secret: true },
  { key: "instagram_app_secret", label: "Instagram App Secret", secret: true },
  { key: "instagram_app_id", label: "Instagram App ID", secret: false },
  { key: "instagram_business_account_id", label: "Instagram Business Account ID", secret: false },
];

export default function InstagramSecrets() {
  const [status, setStatus] = useState(null);
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");

  const loadStatus = () =>
    crmApi.instagramStatus().then((d) => setStatus(d.secrets)).catch((e) => setErr(e.message));

  useEffect(() => { loadStatus(); }, []);

  const save = async () => {
    const body = Object.fromEntries(
      Object.entries(form).filter(([, v]) => (v || "").trim() !== "")
    );
    if (Object.keys(body).length === 0) { setErr("Paste at least one value before saving."); return; }
    setBusy(true); setErr(""); setMsg("");
    try {
      const d = await crmApi.instagramSaveSecrets(body);
      setStatus(d.secrets);
      setForm({});
      setMsg(`Saved securely (${d.saved.length} value${d.saved.length > 1 ? "s" : ""}). Values are never shown again — only masked status.`);
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="bg-white border border-[#6E1F2C]/10 p-5 space-y-4" data-testid="crm-instagram-secrets">
      <p className="text-[10px] tracking-[0.26em] uppercase text-[#6E1F2C] flex items-center gap-2">
        <Instagram size={13} /> Instagram Integration — Secure Credentials
      </p>
      <p className="text-[11px] text-[#7A6B5F] flex items-start gap-2">
        <ShieldCheck size={14} className="shrink-0 mt-0.5 text-[#3F6E2C]" />
        Write-only vault: values are stored as protected backend secrets (server .env), never in the
        database, source code or browser. Once saved, only a masked status is shown.
      </p>
      {FIELDS.map((f) => {
        const st = status?.[f.key];
        return (
          <Field key={f.key} label={f.label}>
            <div className="flex items-center gap-3">
              <input
                type="password"
                autoComplete="new-password"
                className={inputCls}
                data-testid={`ig-secret-${f.key}`}
                value={form[f.key] || ""}
                onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                placeholder={st?.configured ? "Configured — paste to replace" : "Paste value here"}
              />
              <span
                className={`text-[10px] whitespace-nowrap ${st?.configured ? "text-[#3F6E2C]" : "text-[#A6822F]"}`}
                data-testid={`ig-status-${f.key}`}
              >
                {st ? (st.configured ? `✓ ${st.masked}` : "Not set") : "…"}
              </span>
            </div>
          </Field>
        );
      })}
      {err && <p className="text-xs text-[#A3141A]" data-testid="ig-secrets-error">{err}</p>}
      {msg && <p className="text-xs text-[#3F6E2C]" data-testid="ig-secrets-saved">{msg}</p>}
      <button
        type="button"
        onClick={save}
        disabled={busy}
        data-testid="ig-secrets-save"
        className="bg-[#6E1F2C] text-[#FAF5EC] px-5 py-2 text-[10px] tracking-[0.24em] uppercase hover:bg-[#56151F] disabled:opacity-60"
      >
        {busy ? "Saving securely…" : "Save Secrets"}
      </button>
    </div>
  );
}
